import { useState } from "react";
import { hotGroups } from "@/mocks/melon";

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line",
  down: "ri-arrow-down-line",
  flat: "ri-subtract-line",
  new: "ri-sparkling-line",
};
const trendColor: Record<string, string> = {
  up: "text-primary-500",
  down: "text-secondary-500",
  flat: "text-foreground-400",
  new: "text-accent-600",
};

const WORDCLOUD = [
  "出圈", "加更", "实锤", "破防", "塌房", "追更", "二创", "名场面", "高能", "考古", "凡尔赛", "名场面重演", "真香", "上头", "无缝衔接",
];

const HEAT_BARS = [
  { t: "晚间 19–21 点", v: 92 },
  { t: "午间 11–13 点", v: 74 },
  { t: "早间 7–9 点", v: 56 },
  { t: "午夜空档 23–1 点", v: 41 },
];

export default function HotRank() {
  const [active, setActive] = useState(hotGroups[0].group);

  const current = hotGroups.find((g) => g.group === active) || hotGroups[0];

  return (
    <section id="hot" className="bg-background-100/70">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-2 text-xs font-bold tracking-widest text-primary-500">TRENDING</p>
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">全网热榜</h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">
              实时梳理各平台被讨论最多的话题，点击栏目即可切换查看不同领域正在升温的内容。
            </p>
          </div>
        </div>

        {/* 栏目切换 */}
        <div className="mt-7 flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
          {hotGroups.map((g) => (
            <button
              key={g.group}
              type="button"
              onClick={() => setActive(g.group)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                active === g.group
                  ? "bg-foreground-950 font-medium text-background-50"
                  : "text-foreground-600 hover:bg-background-100"
              }`}
            >
              {g.group}
            </button>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* 榜单 */}
          <div className="lg:col-span-8">
            <ol className="divide-y divide-background-200 overflow-hidden rounded-2xl border border-background-200 bg-background-50">
              {current.list.map((it) => (
                <li key={it.rank} className="group">
                  <a href="#hot" className="flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-background-50">
                    <span
                      className={`w-7 shrink-0 text-center font-heading text-lg font-black leading-none ${
                        it.rank <= 3 ? "text-primary-500" : "text-background-300"
                      }`}
                    >
                      {it.rank}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600 md:text-[15px]">
                        {it.title}
                      </p>
                      <div className="mt-1 flex items-center gap-2 text-xs text-foreground-400">
                        <span className="rounded bg-background-100 px-1.5 py-0.5 text-foreground-500">{it.tag}</span>
                        <span>{it.time}</span>
                      </div>
                    </div>
                    <div className="hidden items-center gap-2 sm:flex">
                      <span className="text-sm text-foreground-600">{it.heat}</span>
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full bg-background-100 ${trendColor[it.trend]}`}>
                        <i className={`${trendIcon[it.trend]} text-sm`} aria-hidden="true" />
                      </span>
                    </div>
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {/* 侧栏：热词 + 时段 */}
          <aside className="space-y-5 lg:col-span-4" aria-label="热词与热力">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                <i className="ri-price-tag-3-line text-primary-500" aria-hidden="true" />
                今日网络热词
              </h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {WORDCLOUD.map((w, i) => (
                  <span
                    key={w}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs ${
                      i % 4 === 0
                        ? "bg-primary-100 text-primary-700"
                        : i % 4 === 1
                        ? "bg-accent-100 text-accent-800"
                        : i % 4 === 2
                        ? "bg-secondary-100 text-secondary-700"
                        : "bg-background-100 text-foreground-600"
                    }`}
                  >
                    {w}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                <i className="ri-bar-chart-2-line text-accent-600" aria-hidden="true" />
                讨论高峰时段
              </h3>
              <ul className="mt-4 space-y-3">
                {HEAT_BARS.map((b) => (
                  <li key={b.t}>
                    <div className="flex items-center justify-between text-xs text-foreground-600">
                      <span>{b.t}</span>
                      <span className="text-foreground-400">{b.v}%</span>
                    </div>
                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-background-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-primary-400 to-accent-400"
                        style={{ width: `${b.v}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}